import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState({
    logo: null,
    tenants: [],
    permissions: [],
    roles: [],
    traininggroupId: null,
    traininggroupName: null,
    selectedTenant: null,
    navigation: []
  });

  // Load initial values from localStorage
  useEffect(() => {
    const loadUserInfo = () => {
      const logo = localStorage.getItem('logo');
      const tenants = localStorage.getItem('tenants');
      const permissions = localStorage.getItem('permissions');
      const roles = localStorage.getItem('roles');
      const traininggroupId = localStorage.getItem('traininggroupId');
      const traininggroupName = localStorage.getItem('traininggroupName');
      const selectedTenant = localStorage.getItem('selectedTenant');
      const navigation = localStorage.getItem('navigation');

      setUserInfo({
        logo: logo || null,
        tenants: tenants ? JSON.parse(tenants) : [],
        permissions: permissions ? JSON.parse(permissions) : [],
        roles: roles ? JSON.parse(roles) : [],
        traininggroupId: traininggroupId ? JSON.parse(traininggroupId) : null,
        traininggroupName: traininggroupName ? JSON.parse(traininggroupName) : null,
        selectedTenant: selectedTenant || null,
        navigation: navigation ? JSON.parse(navigation) : []
      });
    };

    loadUserInfo();

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (['logo', 'tenants', 'permissions', 'roles', 'traininggroupId', 'traininggroupName', 'selectedTenant', 'navigation'].includes(e.key)) {
        loadUserInfo();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Function to update user info and localStorage
  const updateUserInfo = useCallback((newData) => {
    setUserInfo(prev => {
      const updated = { ...prev, ...newData };
      
      // Update localStorage
      if (newData.logo !== undefined) {
        if (newData.logo) {
          localStorage.setItem('logo', newData.logo);
        } else {
          localStorage.removeItem('logo');
        }
      }
      if (newData.tenants !== undefined) {
        localStorage.setItem('tenants', JSON.stringify(newData.tenants));
      }
      if (newData.permissions !== undefined) {
        localStorage.setItem('permissions', JSON.stringify(newData.permissions));
      }
      if (newData.roles !== undefined) {
        localStorage.setItem('roles', JSON.stringify(newData.roles));
      }
      if (newData.traininggroupId !== undefined) {
        localStorage.setItem('traininggroupId', JSON.stringify(newData.traininggroupId));
      }
      if (newData.traininggroupName !== undefined) {
        localStorage.setItem('traininggroupName', JSON.stringify(newData.traininggroupName));
      }
      if (newData.selectedTenant !== undefined) {
        localStorage.setItem('selectedTenant', newData.selectedTenant);
      }
      if (newData.navigation !== undefined) {
        localStorage.setItem('navigation', JSON.stringify(newData.navigation));
      }

      return updated;
    });
  }, []);

  const setSelectedTenant = useCallback((tenantId) => {
    updateUserInfo({ selectedTenant: tenantId });
  }, [updateUserInfo]);

  const value = {
    ...userInfo,
    updateUserInfo,
    setSelectedTenant
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
